import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Mail, Lock, User, Loader2, Check, X, Navigation, Search, Package, ArrowRight, CheckCircle2, Eye, EyeOff, AlertCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common disposable email domains — backend does the authoritative check; this is UX-only
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'mailinator.com','guerrillamail.com','guerrillamail.net','guerrillamail.org',
  'guerrillamail.biz','guerrillamail.de','guerrillamail.info','trashmail.com',
  'trashmail.net','trashmail.org','trashmail.me','trashmail.at','trashmail.io',
  'tempmail.com','temp-mail.org','temp-mail.io','10minutemail.com','10minutemail.net',
  'yopmail.com','yopmail.fr','cool.fr.nf','jetable.fr.nf','nospam.ze.tc',
  'nomail.xl.cx','mega.zik.dj','speed.1s.fr','courriel.fr.nf','moncourrier.fr.nf',
  'monemail.fr.nf','monmail.fr.nf','sharklasers.com','guerrillamailblock.com',
  'grr.la','guerrillamail.info','spam4.me','mailnull.com','spamgourmet.com',
  'spamgourmet.net','spamgourmet.org','mytemp.email','fakeinbox.com',
  'maildrop.cc','mailsac.com','throwaway.email','dispostable.com',
  'mailnesia.com','harakirimail.com','tempr.email','getnada.com',
  'mohmal.com','anonbox.net','spambox.us','spambox.info','spambox.org',
  'spambox.me','spamoff.de','spamfree24.org','trashdevil.com','trashdevil.de',
  'wegwerfmail.de','wegwerfmail.net','wegwerfmail.org','spamex.com',
  'mailtemp.info','tempinbox.com','throwam.com','discard.email',
  'filzmail.com','spamfree.eu','noclickemail.com','nospam4.us','no-spam.ws',
]);

function isDisposableEmail(emailAddress) {
  if (!emailAddress || !emailAddress.includes('@')) return false;
  const domain = emailAddress.split('@').pop().toLowerCase();
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) return true;
  const patterns = [
    'mailinator','guerrillamail','trashmail','spamgourmet','10minutemail',
    'tempmail','temp-mail','disposablemail','throwawayemail','throwaway-email',
    'dispostable','fakeinbox','nospammail','yopmail',
  ];
  return patterns.some(p => domain.includes(p));
}

// Facebook OAuth configuration
const FACEBOOK_APP_ID = '759058827246526';
const FACEBOOK_REDIRECT_URI = 'https://wantd.au/auth/facebook/callback';

const INTENT_OPTIONS = [
  {
    id: 'buyer',
    title: 'Buy with Want Ads',
    subtitle: 'Post what you\'re looking for',
    description: 'Create a "Want Ad" describing what you need. Sellers with matching items will be notified and can reach out to you. No more endless scrolling!',
    icon: Search,
    color: 'tan',
  },
  {
    id: 'seller',
    title: 'Sell with Passive Listings',
    subtitle: 'List items without the hassle',
    description: 'List your items once and forget about it. When a buyer posts a matching Want, you\'ll be notified automatically. Selling made effortless.',
    icon: Package,
    color: 'navy',
  },
  {
    id: 'both',
    title: 'Do Both',
    subtitle: 'The full WANTD experience',
    description: 'Most users choose this! Post Wants for things you need AND list items you\'re willing to sell. Get the best of both worlds.',
    icon: ArrowRight,
    color: 'green',
  },
];

export default function RegisterPage() {
  const [step, setStep] = useState(2); // Skip intent selection, go directly to form
  const [intent, setIntent] = useState('both'); // Default to "both" since we're skipping selection
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Username availability check state
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null = not checked, true = available, false = taken
  const [usernameError, setUsernameError] = useState('');

  // Email validation state
  const [emailError, setEmailError] = useState('');
  
  const { register, resendVerificationEmail, loginWithGoogle } = useAuth();
  const { location, requestLocation, setManualLocation } = useLocation();
  const navigate = useNavigate();

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(async (usernameToCheck) => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameAvailable(null);
      setUsernameError(usernameToCheck.length > 0 ? 'Username must be at least 3 characters' : '');
      return;
    }

    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (!usernameRegex.test(usernameToCheck)) {
      setUsernameAvailable(false);
      setUsernameError('Only letters, numbers, and underscores allowed');
      return;
    }

    setUsernameChecking(true);
    setUsernameError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/check-username?username=${encodeURIComponent(usernameToCheck)}`);
      const data = await response.json();
      
      setUsernameAvailable(data.available);
      if (!data.available) {
        setUsernameError('This username is already taken');
      }
    } catch (error) {
      console.error('Username check failed:', error);
      // Don't block registration on check failure
      setUsernameAvailable(null);
    } finally {
      setUsernameChecking(false);
    }
  }, []);

  // Debounce username check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (username) {
        checkUsernameAvailability(username);
      } else {
        setUsernameAvailable(null);
        setUsernameError('');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, checkUsernameAvailability]);

  // Handle Google Sign-Up via Firebase
  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle();
      
      toast.success('Welcome to WANTD! Account created with Google.');
      
      // Redirect based on role
      if (result.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      if (error.response?.data?.detail?.error === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else {
        console.error('Google signup error:', error);
        if (error.message !== 'Sign-in cancelled') {
          toast.error(error.message || 'Failed to sign up with Google');
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle Facebook OAuth - initiate the flow
  const handleFacebookSignup = () => {
    setFacebookLoading(true);
    
    // Redirect to Facebook OAuth
    const facebookAuthUrl = 
      `https://www.facebook.com/v18.0/dialog/oauth` +
      `?client_id=${FACEBOOK_APP_ID}` +
      `&redirect_uri=${encodeURIComponent(FACEBOOK_REDIRECT_URI)}` +
      `&scope=email,public_profile` +
      `&response_type=code`;
    
    window.location.href = facebookAuthUrl;
  };

  const handleEmailBlur = () => {
    if (email && isDisposableEmail(email)) {
      setEmailError('Please use a permanent email address. Temporary or disposable email addresses are not accepted.');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!intent) {
      toast.error('Please select what brings you here');
      return;
    }

    if (isDisposableEmail(email)) {
      setEmailError('Please use a permanent email address. Temporary or disposable email addresses are not accepted.');
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        email,
        username,
        first_name: firstName,
        last_name: lastName,
        password,
        account_type: 'both',
        location: { lat: location.lat, lng: location.lng },
        location_name: location.name,
      });
      
      if (result.requiresVerification) {
        // Show verification required screen
        setRegisteredEmail(result.email);
        setRegistrationComplete(true);
        toast.success('Account created! Please check your email to verify.');
        // Sign-up bonus toast (backend issues async; show optimistic message)
        setTimeout(() => {
          toast.success('500 bonus points added to your account!');
        }, 1500);
      } else if (result.user) {
        // Fallback for any legacy behavior
        toast.success(`Welcome to WANTD, ${result.user.first_name || result.user.username}!`);
        setTimeout(() => toast.success('500 bonus points added to your account!'), 1000);
        navigate(`/welcome?intent=${intent}`);
      }
    } catch (error) {
      if (error.response?.data?.detail?.error === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else {
        const errorCode = error.code;
        let errorMessage = 'Registration failed';

        if (errorCode === 'auth/email-already-in-use') {
          errorMessage = 'This email is already registered. Please sign in instead.';
        } else if (errorCode === 'auth/weak-password') {
          errorMessage = 'Password should be at least 6 characters.';
        } else if (errorCode === 'auth/invalid-email') {
          errorMessage = 'Please enter a valid email address.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail();
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email. Please try signing in first.');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (option, isSelected) => {
    if (!isSelected) return 'border-[var(--theme-border)] hover:border-[#D4CCC2]';
    
    switch (option.color) {
      case 'tan':
        return 'border-[#C9956C] bg-[#C9956C]/10';
      case 'navy':
        return 'border-[#1E3A5F] bg-[#1E3A5F]/10';
      case 'green':
        return 'border-[#059669] bg-[#059669]/10';
      default:
        return 'border-[var(--theme-border)]';
    }
  };

  const getTextColor = (option, isSelected) => {
    if (!isSelected) return 'text-[var(--theme-text-primary)]';
    
    switch (option.color) {
      case 'tan':
        return 'text-[#C9956C]';
      case 'navy':
        return 'text-[#1E3A5F] dark:text-[#C9956C]';
      case 'green':
        return 'text-[#059669]';
      default:
        return 'text-[var(--theme-text-primary)]';
    }
  };

  const getCheckBg = (option) => {
    switch (option.color) {
      case 'tan':
        return 'bg-[#C9956C]';
      case 'navy':
        return 'bg-[#1E3A5F]';
      case 'green':
        return 'bg-[#059669]';
      default:
        return 'bg-[#1E3A5F]';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative" style={{ backgroundColor: 'var(--theme-bg)' }}>
      {/* Close Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-[var(--theme-card)] border border-[var(--theme-border)] flex items-center justify-center hover:bg-[var(--theme-hover)] transition-colors shadow-sm z-10"
        title="Back to Home"
      >
        <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
      </button>

      <div className="w-full max-w-lg">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-wantd-navy.png"
            alt="WANTD" 
            className="h-12 w-auto"
          />
        </Link>

        {/* Registration Complete - Verify Email */}
        {registrationComplete ? (
          <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card text-center">
            <div className="w-16 h-16 bg-[#059669]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#059669]" />
            </div>
            
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
              Check Your Email
            </h1>
            
            <p className="text-[var(--theme-text-secondary)] mb-6">
              We&apos;ve sent a verification link to:
            </p>
            
            <p className="font-medium text-[var(--theme-text-primary)] bg-[var(--theme-bg)] px-4 py-3 rounded-lg mb-6">
              {registeredEmail}
            </p>
            
            <p className="text-sm text-[var(--theme-text-muted)] mb-6">
              Click the link in your email to verify your account. Once verified, you can sign in and start using WANTD.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                data-testid="go-to-login-btn"
              >
                Go to Sign In
              </Button>
              
              <button
                onClick={handleResendVerification}
                disabled={loading}
                className="text-sm text-[#C9956C] hover:text-[#B8845B] font-medium"
              >
                {loading ? 'Sending...' : "Didn't receive the email? Resend"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Card */}
            <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
              {step === 1 ? (
                <>
                  <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-2">
                    How do you want to use WANTD?
                  </h1>
                  <p className="text-[var(--theme-text-secondary)] text-center mb-6">
                    Click on an option to learn how it works
                  </p>

              <div className="space-y-3">
                {INTENT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = intent === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => setIntent(isSelected ? '' : option.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${getColorClasses(option, isSelected)}`}
                      data-testid={`intent-${option.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? getCheckBg(option) : 'bg-[var(--theme-hover)]'}`}>
                          {isSelected ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <Icon className={`w-5 h-5 ${option.color === 'tan' ? 'text-[#C9956C]' : option.color === 'navy' ? 'text-[#1E3A5F]' : 'text-[#059669]'}`} />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-bold ${getTextColor(option, isSelected)}`}>
                            {option.title}
                          </h3>
                          <p className={`text-xs mt-0.5 ${isSelected ? 'text-[var(--theme-text-secondary)]' : 'text-[var(--theme-text-muted)]'}`}>
                            {option.subtitle}
                          </p>
                          {isSelected && (
                            <p className="text-sm text-[var(--theme-text-secondary)] mt-2 leading-relaxed">
                              {option.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-[var(--theme-text-muted)] text-center mt-4">
                You can always change this later in your settings.
              </p>

              <Button
                onClick={() => intent && setStep(2)}
                className="w-full mt-6 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12 dark:bg-[#C9956C] dark:text-[#1E3A5F] dark:hover:bg-[#B8845B]"
                disabled={!intent}
                data-testid="continue-btn"
              >
                Continue
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[var(--theme-border)]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[var(--theme-card)] text-[var(--theme-text-muted)]">or sign up with</span>
                </div>
              </div>

              {/* Social Signup Options */}
              <div className="space-y-3">
                {/* Google Signup */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[#4285F4]/10 hover:border-[#4285F4]/50 rounded-full h-12"
                  onClick={handleGoogleSignup}
                  disabled={googleLoading}
                  data-testid="google-signup-btn"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </Button>

                {/* Facebook Signup - Coming Soon */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full border-[var(--theme-border)] text-[var(--theme-text-muted)] rounded-full h-12 cursor-not-allowed opacity-60"
                  disabled={true}
                  data-testid="facebook-signup-btn"
                >
                  <svg className="w-5 h-5 mr-2 opacity-50" fill="#9CA3AF" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continue with Facebook
                  <span className="ml-2 text-xs bg-[var(--theme-hover)] px-2 py-0.5 rounded-full">Coming Soon</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-2">
                Create your account
              </h1>
              <p className="text-[var(--theme-text-secondary)] text-center mb-8">
                Fill in your details to get started
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* First Name & Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name</label>
                    <Input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      required
                      data-testid="register-firstname-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name</label>
                    <Input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      required
                      data-testid="register-lastname-input"
                    />
                  </div>
                </div>

                {/* Username */}
                <div>
                  <label className="form-label flex items-center gap-1">
                    Username
                    <div className="relative group">
                      <Info className="w-4 h-4 text-[var(--theme-text-muted)] cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E3A5F] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                        Choose a unique username that other WANTD users will see on your profile. Have fun with it!
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1E3A5F]"></div>
                      </div>
                    </div>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                      placeholder="johndoe"
                      className={`pl-10 pr-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)] ${
                        usernameError ? 'border-red-500 focus:border-red-500' : 
                        usernameAvailable === true ? 'border-green-500 focus:border-green-500' : ''
                      }`}
                      required
                      data-testid="register-username-input"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {usernameChecking ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />
                      ) : usernameAvailable === true ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : usernameAvailable === false ? (
                        <X className="w-5 h-5 text-red-500" />
                      ) : null}
                    </div>
                  </div>
                  {usernameError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {usernameError}
                    </p>
                  )}
                  {usernameAvailable === true && !usernameError && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Username is available
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="form-label">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                      onBlur={handleEmailBlur}
                      placeholder="you@example.com"
                      className={`pl-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)] ${emailError ? 'border-red-500 focus:border-red-500' : ''}`}
                      required
                      autoComplete="off"
                      data-testid="register-email-input"
                    />
                  </div>
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="form-label">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      minLength={6}
                      required
                      autoComplete="new-password"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)] transition-colors"
                      data-testid="register-password-toggle"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    Must be at least 6 characters
                  </p>
                </div>

                {/* Location */}
                <div>
                  <label className="form-label">Your Location</label>
                  <LocationAutocomplete
                    value={location.name}
                    onSelect={(locationData) => {
                      setManualLocation({
                        lat: locationData.lat,
                        lng: locationData.lng,
                        name: locationData.name
                      });
                    }}
                    placeholder="Search suburb or postcode..."
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-[var(--theme-text-muted)]">
                      Location helps match you with nearby buyers/sellers
                    </p>
                    <button
                      type="button"
                      onClick={requestLocation}
                      className="text-xs text-[#C9956C] hover:text-[#B8845B] font-medium flex items-center gap-1"
                    >
                      <Navigation className="w-3 h-3" />
                      Use my location
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 border-[var(--theme-border)] text-[var(--theme-text-primary)] rounded-full h-12"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full h-12"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[var(--theme-border)]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[var(--theme-card)] text-[var(--theme-text-muted)]">or sign up with</span>
                </div>
              </div>

              {/* Social Signup Options */}
              <div className="space-y-3">
                {/* Google Signup */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[#4285F4]/10 hover:border-[#4285F4]/50 rounded-full h-12"
                  onClick={handleGoogleSignup}
                  disabled={googleLoading}
                  data-testid="google-signup-btn-step2"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </Button>

                {/* Facebook Signup - Coming Soon */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full border-[var(--theme-border)] text-[var(--theme-text-muted)] rounded-full h-12 cursor-not-allowed opacity-60"
                  disabled={true}
                  data-testid="facebook-signup-btn-step2"
                >
                  <svg className="w-5 h-5 mr-2 opacity-50" fill="#9CA3AF" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continue with Facebook
                  <span className="ml-2 text-xs bg-[var(--theme-hover)] px-2 py-0.5 rounded-full">Coming Soon</span>
                </Button>
              </div>
            </>
          )}

          {/* Sign in link */}
          <p className="text-center text-sm text-[var(--theme-text-secondary)] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#C9956C] hover:text-[#B8845B] font-medium">
              Sign in
            </Link>
          </p>

          {/* Legal links */}
          <p className="text-center text-xs text-[var(--theme-text-muted)] mt-4">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#C9956C] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#C9956C] hover:underline">Privacy Policy</Link>
          </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
